/**
 * 🎲 Seeded randomness (v5.54, designed with Javi for the DAILY RUN):
 * everyone who feeds the same seed string gets the same stream, so a
 * daily's map, draft and ascension are identical for every player on
 * Earth. The seed also renders as a short shareable CODE — the door to
 * "play my seed" later without new architecture.
 *
 * xmur3 (string → 128 bits of seed) + sfc32 (fast, solid PRNG) — the
 * standard deterministic-JS pairing; no Math.random anywhere.
 */
import { swapRandomSource } from './random'

/** String hash → a stream of 32-bit seeds. */
function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353)
    h = (h << 13) | (h >>> 19)
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507)
    h = Math.imul(h ^ (h >>> 13), 3266489909)
    h ^= h >>> 16
    return h >>> 0
  }
}

function sfc32(a: number, b: number, c: number, d: number): () => number {
  return () => {
    a >>>= 0; b >>>= 0; c >>>= 0; d >>>= 0
    let t = (a + b) | 0
    a = b ^ (b >>> 9)
    b = (c + (c << 3)) | 0
    c = (c << 21) | (c >>> 11)
    d = (d + 1) | 0
    t = (t + d) | 0
    c = (c + t) | 0
    return (t >>> 0) / 4294967296
  }
}

/** A deterministic drop-in for Math.random, derived from a seed string. */
export function seededRng(seed: string): () => number {
  const s = xmur3(seed)
  return sfc32(s(), s(), s(), s())
}

/** 🗓️ v5.63 (per Javi): the expedition turned WEEKLY — the key is the
 * UTC week's MONDAY, so the whole world turns over together, once a
 * week. Local twin of shared/leaderboard's weeklyDateKey (test bundles
 * alias only `~`); the two are pinned equal in tests. */
export function weeklyDateKey(now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7))
  return d.toISOString().slice(0, 10)
}

/** The week's full seed string. `weekly-` prefix, NOT `daily-`: Monday
 * dates already served as daily seeds (and board doc ids) before the
 * cadence change — the prefix keeps old worlds and new apart. */
export function weeklySeed(dateKey: string = weeklyDateKey()): string {
  return `weekly-${dateKey}`
}

/** 🔤 The seed as a shareable CODE (Crockford base32, XXXX-XXXX):
 * deterministic from the seed string, friendly to read aloud. */
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
export function seedCode(seed: string): string {
  const s = xmur3(seed)
  const a = s()
  const b = s()
  let out = ''
  for (let i = 0; i < 4; i++) out += CROCKFORD[(a >>> (i * 5)) & 31]
  out += '-'
  for (let i = 0; i < 4; i++) out += CROCKFORD[(b >>> (i * 5)) & 31]
  return out
}

/** 🎖️ The daily's ascension — seeded, A2 through A9 (per Javi). */
export function dailyAscension(seed: string): number {
  const s = xmur3(`${seed}/ascension`)
  return 2 + (s() % 8)
}

/**
 * 🦴 v5.57 (per Javi: "the only thing I don't want in the skeleton is
 * the mission success roll"): run `fn` under a seeded stream keyed off
 * the daily's seed + a stable context key — per NODE, so the skeleton
 * is path-independent: whichever route you take, node X reveals the
 * same mystery, deals the same reward cards, stocks the same shelves
 * and spawns the same missions. On a campaign run this is a pass-
 * through (live dice). Nest-safe: an inner stream restores the outer
 * one, never live play, on exit.
 *
 * KEY DISCIPLINE: a key must be consumed at most once per run per
 * game-state (node entry, Nth spawn, a card picked) — replaying a key
 * against the same state deals the same result, which is the point.
 */
export function withRunStream<T>(
  run: { mode?: string; seed?: string },
  key: string,
  fn: () => T,
): T {
  if (run.mode !== 'daily' || !run.seed) return fn()
  const prev = swapRandomSource(seededRng(`${run.seed}/${key}`))
  try {
    return fn()
  } finally {
    swapRandomSource(prev)
  }
}
